import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

import type { PreviewRecordsConnection } from '@src/apis/reware/catalog/validateCatalogUpload';

interface PreviewRecordsTableProps {
  previewRecords: PreviewRecordsConnection;
  totalValidationErrors?: number;
  pageSize?: number;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onPageSizeChange?: (pageSize: number) => void;
}

const PreviewRecordsTable: React.FC<PreviewRecordsTableProps> = ({
  previewRecords,
  totalValidationErrors,
  pageSize = 50,
  onNextPage,
  onPreviousPage,
  onPageSizeChange,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Limit records to the requested page size to handle error bubbling
  const allRecords = previewRecords.edges.map(edge => edge.node);
  const records = allRecords.slice(0, pageSize);
  const { hasNextPage, hasPreviousPage } = previewRecords.pageInfo;

  const toggleRow = (rowNumber: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowNumber)) {
        newSet.delete(rowNumber);
      } else {
        newSet.add(rowNumber);
      }
      return newSet;
    });
  };

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const getMainImageUrl = (record: typeof records[0]): string | null => {
    // For new records (action='create'), look in values; for updates, look in existingValues
    const sourceData = record.action === 'create' ?
      (record.values as Record<string, unknown>) :
      (record.existingValues as Record<string, unknown> | null);

    if (!sourceData) return null;

    let partnerImageUrls = sourceData.partner_image_urls;

    // Handle string format: "{main:url,alt1:url,...}"
    if (typeof partnerImageUrls === 'string') {
      try {
        // Remove outer braces and parse key:value pairs
        const content = partnerImageUrls.replace(/^\{|\}$/g, '').trim();
        if (!content) return null;

        const parsed: Record<string, string> = {};
        // Split by comma, but be careful with URLs containing commas
        const pairs = content.split(/,(?=\w+:)/);
        pairs.forEach((pair) => {
          const colonIndex = pair.indexOf(':');
          if (colonIndex > 0) {
            const key = pair.substring(0, colonIndex).trim();
            const value = pair.substring(colonIndex + 1).trim();
            parsed[key] = value;
          }
        });
        partnerImageUrls = parsed;
      } catch {
        return null;
      }
    }

    // Now access the main image
    const imageData = partnerImageUrls as Record<string, unknown> | undefined;
    const mainImage = imageData?.main;

    if (!mainImage) return null;

    // Handle single URL string
    if (typeof mainImage === 'string' && mainImage.trim().length > 0) {
      return mainImage.trim();
    }

    // Handle array of URLs
    if (Array.isArray(mainImage) && mainImage.length > 0) {
      return String(mainImage[0]);
    }

    return null;
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2">
          Preview Records (
          {records.length}
          {' '}
          of
          {' '}
          {previewRecords.totalCount}
          {' '}
          shown)
        </Typography>
        {totalValidationErrors !== undefined && totalValidationErrors > 0 && (
          <Chip
            icon={<ErrorIcon />}
            label={`${totalValidationErrors} validation error${totalValidationErrors > 1 ? 's' : ''}`}
            color="error"
            size="small"
          />
        )}
      </Box>

      <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell width={60}>Row</TableCell>
              <TableCell width={80}>Image</TableCell>
              <TableCell width={90}>Action</TableCell>
              <TableCell sx={{ maxWidth: 400 }}>Values</TableCell>
              <TableCell width={80} align="center">
                Errors
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((record) => {
              const isExpanded = expandedRows.has(record.rowNumber);
              const hasErrors = record.validationErrors.length > 0;

              return (
                <React.Fragment key={record.rowNumber}>
                  <TableRow
                    hover
                    sx={{
                      cursor: hasErrors ? 'pointer' : 'default',
                      backgroundColor: hasErrors ? 'error.lighter' : undefined,
                      '&:hover': hasErrors ? { backgroundColor: 'error.light' } : undefined,
                    }}
                    onClick={() => hasErrors && toggleRow(record.rowNumber)}
                  >
                    <TableCell>
                      {hasErrors && (
                        <IconButton size="small">
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>{record.rowNumber}</TableCell>
                    <TableCell>
                      {getMainImageUrl(record) ? (
                        <Box
                          component="img"
                          src={getMainImageUrl(record)!}
                          alt={`Product ${record.sku}`}
                          sx={{
                            width: 60,
                            height: 60,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: 1,
                            borderColor: 'divider',
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'grey.100',
                            borderRadius: 1,
                            border: 1,
                            borderColor: 'divider',
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                          }}
                        >
                          No image
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={record.action}
                        size="small"
                        color={record.action === 'create' ? 'success' : 'info'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 400 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {Object.entries(record.values).map(([key, value]) => {
                          const hasError = record.validationErrors.some((err) => err.field === key);
                          return (
                            <Chip
                              key={key}
                              label={`${key}: ${formatValue(value)}`}
                              size="small"
                              color={hasError ? 'error' : 'default'}
                              variant={hasError ? 'filled' : 'outlined'}
                            />
                          );
                        })}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {hasErrors && (
                        <Chip
                          icon={<ErrorIcon />}
                          label={record.validationErrors.length}
                          color="error"
                          size="small"
                        />
                      )}
                    </TableCell>
                  </TableRow>

                  {hasErrors && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              Validation Errors:
                            </Typography>

                            {record.validationErrors.map((error, idx) => (
                              <Alert
                                key={idx}
                                severity={error.severity}
                                icon={error.severity === 'error' ? <ErrorIcon /> : <WarningIcon />}
                                sx={{ mb: 1 }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {error.field}
                                </Typography>
                                <Typography variant="body2">
                                  Value: <code>{formatValue(error.value)}</code>
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  {error.message}
                                </Typography>
                              </Alert>
                            ))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {(onNextPage || onPreviousPage || onPageSizeChange) && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            {onPageSizeChange && (
              <FormControl size="small">
                <Select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  sx={{ minWidth: 80 }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Showing
              {' '}
              {records.length}
              {' '}
              of
              {' '}
              {previewRecords.totalCount}
            </Typography>
            {onPreviousPage && (
              <Button
                size="small"
                startIcon={<NavigateBeforeIcon />}
                onClick={onPreviousPage}
                disabled={!hasPreviousPage}
              >
                Previous
              </Button>
            )}
            {onNextPage && (
              <Button
                size="small"
                endIcon={<NavigateNextIcon />}
                onClick={onNextPage}
                disabled={!hasNextPage}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default PreviewRecordsTable;
